import { AfterViewInit, Component, inject, input, OnInit, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TouristGuidesRateDialogComponent } from './tourist-guides-rate-dialog/tourist-guides-rate-dialog.component';
import { TouristGuidesRateDeleteDialogComponent } from './tourist-guides-rate-delete-dialog/tourist-guides-rate-delete-dialog.component';
import { TouristGuidesRatesApiService } from './_api/tourist-guides-rates.api.service';
import { TouristGuideRate } from './_models/tourist-guides-rates.model';
import { catchError, filter, finalize, throwError } from 'rxjs';
import { PricePipe } from '@/common/pipes/price.pipe';
import { FormatDateTimePipe } from '@/common/pipes/format-date-time.pipe';

@Component({
	selector: 'app-tourist-guides-rates',
	standalone: true,
	imports: [
		CommonModule,
		MatTableModule,
		MatButtonModule,
		MatSortModule,
		MatProgressSpinnerModule,
		PricePipe,
		FormatDateTimePipe,
	],
	templateUrl: './tourist-guides-rates.component.html',
	styleUrl: './tourist-guides-rates.component.scss',
})
export class TouristGuidesRatesComponent implements OnInit, AfterViewInit {
	touristGuideId = input.required<string>();
	@ViewChild('dataTable') table: MatTable<TouristGuideRate>;
	@ViewChild(MatSort) sort: MatSort;
	matData = new MatTableDataSource<TouristGuideRate>([]);
	readonly displayedColumns = ['position', 'dateFrom', 'dateTo', 'rate', 'comments', 'actions'];
	rates = signal<TouristGuideRate[]>([]);
	isLoading = signal(false);
	error = signal<string | null>(null);

	private readonly dialog = inject(MatDialog);
	private readonly apiService = inject(TouristGuidesRatesApiService);

	ngOnInit(): void {
		this.loadRates();
	}

	ngAfterViewInit() {
		this.matData.sort = this.sort;
	}

	loadRates(): void {
		if (!this.touristGuideId()) {
			return;
		}

		this.isLoading.set(true);
		this.error.set(null);

		this.apiService
			.getRates(this.touristGuideId())
			.pipe(
				catchError((err) => {
					this.error.set('Nie udało się załadować stawek');
					return throwError(() => err);
				}),
				finalize(() => this.isLoading.set(false)),
			)
			.subscribe((rates) => {
				this.rates.set(rates);
				this.refreshTable();
			});
	}

	addRate() {
		const dialogRef = this.dialog.open(TouristGuidesRateDialogComponent, {
			width: '500px',
			disableClose: true,
			data: {
				rate: null,
				mode: 'add',
				touristGuideId: this.touristGuideId(),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(filter(Boolean))
			.subscribe(() => this.loadRates());
	}

	editRate(rate: TouristGuideRate) {
		const dialogRef = this.dialog.open(TouristGuidesRateDialogComponent, {
			width: '500px',
			disableClose: true,
			data: {
				rate: rate,
				mode: 'edit',
				touristGuideId: this.touristGuideId(),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(filter(Boolean))
			.subscribe(() => this.loadRates());
	}

	deleteRate(rate: TouristGuideRate) {
		const dialogRef = this.dialog.open(TouristGuidesRateDeleteDialogComponent, {
			minWidth: '400px',
			panelClass: 'dialog-padding-20',
			data: {
				rate: rate,
				touristGuideId: this.touristGuideId(),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(filter(Boolean))
			.subscribe(() => this.loadRates());
	}

	private refreshTable() {
		this.matData.data = this.rates();
		if (this.table) {
			this.table.renderRows();
		}
	}
}
